// Radha Rasvyn — hand-authored decorative motifs. All original inline SVG line
// art (no external icon pack, no image assets, no fonts beyond the Google Fonts
// already loaded) — kept restrained and low-opacity so the finance UI underneath
// stays the point. Every stroke uses currentColor or a CSS var, so light/dark
// and the devotional palette carry through automatically.
import type { CSSProperties } from "react";

interface MotifProps { className?: string; style?: CSSProperties }

/** A lotus-petal corner flourish — used sparingly (nav wordmark, hero corner). */
export function LotusFlourish({ size = 22, className, style }: MotifProps & { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" className={className} style={style} aria-hidden="true">
      <g stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" opacity=".9">
        <path d="M16 28 C16 20 16 14 16 6" />
        <path d="M16 26 C11 24 8 19 8 13 C12 14 15 18 16 22" />
        <path d="M16 26 C21 24 24 19 24 13 C20 14 17 18 16 22" />
        <path d="M16 22 C13 19 12 15 13 11 C16 12 17.5 16 17 20" opacity=".7" />
        <path d="M16 22 C19 19 20 15 19 11 C16 12 14.5 16 15 20" opacity=".7" />
      </g>
      <circle cx="16" cy="27" r="1.4" fill="currentColor" />
    </svg>
  );
}

/** A temple-arch silhouette — used as a single large watermark on the hero card. */
export function TempleArch({ width = 340, height = 160, className, style }: MotifProps & { width?: number; height?: number }) {
  return (
    <svg width={width} height={height} viewBox="0 0 340 160" fill="none" className={className} style={style} aria-hidden="true">
      <path
        d="M8 158 L8 92 C8 40 60 6 90 6 C104 6 116 14 116 30 L116 60 L224 60 L224 30 C224 14 236 6 250 6 C280 6 332 40 332 92 L332 158"
        stroke="currentColor" strokeWidth="1.4" opacity=".55" />
      <path d="M60 158 L60 110 C60 80 110 60 170 60 C230 60 280 80 280 110 L280 158"
        stroke="currentColor" strokeWidth="1" opacity=".35" />
      <path d="M170 60 L170 20" stroke="currentColor" strokeWidth="1" opacity=".35" />
      <circle cx="170" cy="14" r="4" stroke="currentColor" strokeWidth="1" opacity=".5" fill="none" />
    </svg>
  );
}

/** A single peacock-feather "eye" — a small accent beside section titles. */
export function PeacockEye({ size = 16, className, style }: MotifProps & { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} style={style} aria-hidden="true">
      <ellipse cx="12" cy="10" rx="7" ry="9" stroke="currentColor" strokeWidth="1" opacity=".6" />
      <ellipse cx="12" cy="10" rx="4" ry="5.5" stroke="currentColor" strokeWidth="1" opacity=".7" />
      <circle cx="12" cy="10" r="2" fill="currentColor" opacity=".85" />
      <path d="M12 19 L12 24" stroke="currentColor" strokeWidth="1" opacity=".4" />
    </svg>
  );
}

/** A flowing river-ripple divider (Yamuna water). Stretches to its container width. */
export function YamunaDivider({ className, style }: MotifProps) {
  return (
    <svg viewBox="0 0 400 16" preserveAspectRatio="none" width="100%" height="16" className={className} style={style} aria-hidden="true">
      <path d="M0 8 C 40 2, 60 14, 100 8 S 160 2, 200 8 S 260 14, 300 8 S 360 2, 400 8"
        fill="none" stroke="currentColor" strokeWidth="1" opacity=".5" />
    </svg>
  );
}

/** A handful of slow-drifting light motes behind the shell. Deterministic
 * positions/timings (no Math.random) so server/client and re-renders agree. */
const PARTICLE_SEEDS = [
  { left: "4%", delay: "0s", duration: "16s", scale: 1, op: .8 },
  { left: "13%", delay: "2s", duration: "21s", scale: .6, op: .5 },
  { left: "24%", delay: "5s", duration: "18s", scale: 1.2, op: .9 },
  { left: "35%", delay: "1s", duration: "24s", scale: .7, op: .55 },
  { left: "46%", delay: "8s", duration: "17s", scale: 1, op: .75 },
  { left: "57%", delay: "3.5s", duration: "22s", scale: .8, op: .6 },
  { left: "68%", delay: "10s", duration: "19s", scale: 1.1, op: .85 },
  { left: "77%", delay: "6.5s", duration: "20s", scale: .65, op: .5 },
  { left: "86%", delay: "0.5s", duration: "23s", scale: 1, op: .7 },
  { left: "94%", delay: "12s", duration: "15s", scale: .9, op: .8 },
];

export function SanctuaryParticles() {
  return (
    <div className="sanctuary-particles">
      {PARTICLE_SEEDS.map((p, i) => (
        <span key={i} style={{
          left: p.left, animationDelay: p.delay, animationDuration: p.duration,
          ["--p-scale" as string]: p.scale, ["--p-op" as string]: p.op,
        } as CSSProperties} />
      ))}
    </div>
  );
}

/** Two or three large, very softly blurred light blooms — the "atmospheric
 * depth" cue, distinct from the small motes above. Fixed behind the shell. */
export function SanctuaryOrbs() {
  return (
    <div className="sanctuary-orbs">
      <i /><i /><i />
    </div>
  );
}
